import React, {
  useMemo,
  useCallback,
  useState,
  useEffect,
  useRef,
} from "react";
import { Box, Button, TextField, Paper, Tooltip } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import PlusIcon from "@material-ui/icons/Add";
import MinusIcon from "@material-ui/icons/Remove";
import InfoIcon from "@material-ui/icons/Info";
import axios from "axios";

// ---- stable props to prevent popup remount/scroll reset ----
const ListboxPropsStable = {
  style: { maxHeight: "350px", scrollBehavior: "auto" },
};

const StyledPaper = React.forwardRef(function StyledPaper(props, ref) {
  return (
    <Paper
      {...props}
      ref={ref}
      style={{ maxHeight: "400px", maxWidth: "450px" }}
    />
  );
});

const LocationsFilter = React.memo(
  ({ name, onChange = () => {}, externalValue }) => {
    const [options, setOptions] = useState([]);
    const [selectedValues, setSelectedValues] = useState([]);
    const [loading, setLoading] = useState(true);

    // fully-controlled input, preserve on selection "reset"
    const [inputValue, setInputValue] = useState("");
    const lastInputRef = useRef("");

    useEffect(() => {
      const fetchLocations = async () => {
        try {
          const { data } = await axios.get(
            `${process.env.REACT_APP_ENDPOINT}/api/public-map/filters`
          );
          const locations = data?.locations || [];
          setOptions(locations);

          // Initialize with all locations selected
          const allValues = locations.map((loc) => loc.value);
          setSelectedValues(allValues);
        } catch (err) {
          console.error("Failed to fetch locations:", err);
        } finally {
          setLoading(false);
        }
      };
      void fetchLocations();
    }, []);

    // Sync internal state with external filter value changes
    useEffect(() => {
      if (externalValue && Array.isArray(externalValue)) {
        setSelectedValues(externalValue);
      }
    }, [externalValue]);

    // Selected option objects derived from selectedValues
    const selectedOptions = useMemo(() => {
      if (!options?.length) return [];
      const set = new Set(selectedValues);
      return options.filter((o) => set.has(o.value));
    }, [selectedValues, options]);

    // Selection change handler
    const handleChange = useCallback(
      (event, newValue) => {
        const newIds = newValue.map((item) => item.value);
        const prevIds = selectedValues;

        const added = newIds.filter((id) => !prevIds.includes(id));
        const removed = prevIds.filter((id) => !newIds.includes(id));

        setSelectedValues(newIds);

        // Notify parent (individual changes to match existing behavior)
        [...added, ...removed].forEach((id) => {
          onChange({
            target: {
              name,
              value: id,
              checked: added.includes(id),
            },
          });
        });
      },
      [selectedValues, onChange, name]
    );

    const handleSelectAll = useCallback(() => {
      const allValues = options.map((opt) => opt.value);
      setSelectedValues(allValues);

      const toAdd = allValues.filter((val) => !selectedValues.includes(val));
      if (toAdd.length > 0) {
        onChange({
          target: { name, value: toAdd, checked: true, batch: true },
        });
      }
    }, [options, onChange, name, selectedValues]);

    const handleSelectNone = useCallback(() => {
      setSelectedValues([]);
      if (selectedValues.length > 0) {
        onChange({
          target: { name, value: selectedValues, checked: false, batch: true },
        });
      }
    }, [selectedValues, onChange, name]);

    if (loading) {
      return (
        <Box display="flex" alignItems="center" gridColumnGap={8}>
          <TextField
            variant="outlined"
            label="Locations"
            placeholder="Loading locations..."
            size="small"
            disabled
            style={{ minWidth: "280px", maxWidth: "350px" }}
          />
        </Box>
      );
    }

    return (
      <Box display="flex" alignItems="center">
        <Tooltip
          title="Deselect specific monitoring locations to turn them off. This list shows all available sites regardless of other filters, but other filters may also hide additional locations on the map."
          placement="top"
        >
          <InfoIcon
            fontSize="small"
            color="primary"
            style={{
              marginRight: "8px",
              cursor: "help",
            }}
          />
        </Tooltip>

        <Box style={{ display: "flex" }}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            disableClearable
            id={`${name}-autocomplete`}
            options={options}
            value={selectedOptions}
            inputValue={inputValue}
            getOptionLabel={(option) => option.display}
            filterSelectedOptions={false}
            onChange={handleChange}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === "reset") {
                // preserve whatever the user typed before selection
                setInputValue(lastInputRef.current);
              } else {
                lastInputRef.current = newInputValue;
                setInputValue(newInputValue);
              }
            }}
            // reduce focus-driven jumps that can trigger remounts
            autoHighlight={false}
            clearOnBlur={false}
            style={{ minWidth: "280px", maxWidth: "350px" }}
            ListboxProps={ListboxPropsStable} // stable identity
            PaperComponent={StyledPaper} // stable identity
            renderTags={() => null} // no chips
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label={
                  selectedOptions.length === 0
                    ? "Locations"
                    : `Locations (${selectedOptions.length} of ${options.length})`
                }
                placeholder="Search monitoring locations..."
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: null, // remove chip container
                  style: {
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                  },
                }}
                InputLabelProps={{
                  ...params.InputLabelProps,
                }}
              />
            )}
            // MUI v4: keep this equality check
            getOptionSelected={(option, value) => option.value === value.value}
            blurOnSelect={false}
          />

          <Tooltip title="Select All" placement="bottom">
            <Button
              variant="outlined"
              size="small"
              onClick={handleSelectAll}
              style={{
                minWidth: "40px",
                padding: "5px",
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                borderLeft: 0,
              }}
            >
              <PlusIcon fontSize="small" />
            </Button>
          </Tooltip>

          <Tooltip title="Select None" placement="bottom">
            <Button
              variant="outlined"
              size="small"
              onClick={handleSelectNone}
              style={{
                minWidth: "40px",
                padding: "5px",
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                borderLeft: 0,
              }}
            >
              <MinusIcon fontSize="small" />
            </Button>
          </Tooltip>
        </Box>
      </Box>
    );
  },
  // memo: only re-render if essential props change
  (prevProps, nextProps) =>
    prevProps.name === nextProps.name &&
    prevProps.onChange === nextProps.onChange
);

export default LocationsFilter;
